import { type PriceHistoryRow, priceSummary } from '@felix/domain';
import { Button, SegmentedControl } from '@mantine/core';
import { IconReceipt } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { formatMoney, formatQuantity } from './inventory.js';
import classes from './Materials.module.css';

/** Varsayılan olarak gösterilen son sipariş satırı. */
const LATEST = 10;

type View = 'all' | 'port' | 'supplier';

/**
 * Malzemenin fiyat geçmişi (ortağın notu #10, #13; ADR 0047 §5): verilmiş siparişlerin fiyatları,
 * liman ve tedarikçi bazında özet. Gemide o geminin, ofiste görebildiği gemilerin siparişleri.
 * Kur yok: para birimleri ayrı satırdır.
 */
export function PriceHistoryPanel({
  rows,
  scope,
}: {
  rows: readonly PriceHistoryRow[];
  scope: 'web' | 'ship';
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`materials.detail.${key}`, options);
  const [view, setView] = useState<View>('all');
  const [all, setAll] = useState(false);
  const money = (value: number, currency: string) => formatMoney(value, currency, locale);
  const shown = all ? rows : rows.slice(0, LATEST);

  return (
    <Panel
      title={k('priceHistory')}
      icon={<IconReceipt size={18} />}
      aria-label={k('priceHistory')}
      extra={
        rows.length > 0 ? (
          <SegmentedControl
            size="xs"
            aria-label={k('priceViews')}
            value={view}
            onChange={(value) => setView(value as View)}
            data={[
              { value: 'all', label: k('allOrders') },
              { value: 'port', label: k('byPort') },
              { value: 'supplier', label: k('bySupplier') },
            ]}
          />
        ) : null
      }
    >
      {rows.length === 0 ? (
        <EmptyState title={k('noPrices')} description={k('priceHistoryHint')} />
      ) : view === 'all' ? (
        <>
          <section
            className={classes.priceWrap}
            aria-label={k('allOrders')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${classes.miniTable} ${classes.priceTable}`}>
              <thead>
                <tr>
                  <th scope="col" aria-sort="descending">
                    {k('orderDate')}
                  </th>
                  <th scope="col">{k('poNo')}</th>
                  {scope === 'web' ? <th scope="col">{k('vessel')}</th> : null}
                  <th scope="col">{k('supplier')}</th>
                  <th scope="col">{k('port')}</th>
                  <th scope="col" data-numeric>
                    {k('qty')}
                  </th>
                  <th scope="col" data-numeric>
                    {k('unitPrice')}
                  </th>
                  <th scope="col" data-numeric>
                    {k('received')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.purchaseOrderLineId}>
                    <td>{formatDay(row.orderDate)}</td>
                    <td>{row.poNo}</td>
                    {scope === 'web' ? <td>{row.vesselName}</td> : null}
                    <td>{row.supplierName}</td>
                    <td>{row.port ?? '—'}</td>
                    <td data-numeric>{`${formatQuantity(row.quantity, locale)} ${row.unit}`}</td>
                    <td data-numeric>{money(row.unitPrice, row.currency)}</td>
                    <td data-numeric>{formatQuantity(row.accepted, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          {rows.length > LATEST ? (
            <Button variant="subtle" size="xs" onClick={() => setAll((v) => !v)}>
              {all ? k('showLatest') : k('viewAll', { count: rows.length })}
            </Button>
          ) : null}
        </>
      ) : (
        <section
          className={classes.priceWrap}
          aria-label={view === 'port' ? k('byPort') : k('bySupplier')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${classes.miniTable} ${classes.priceTable}`}>
            <thead>
              <tr>
                <th scope="col">{view === 'port' ? k('port') : k('supplier')}</th>
                <th scope="col" data-numeric>
                  {k('lastPrice')}
                </th>
                <th scope="col">{k('orderDate')}</th>
                <th scope="col" data-numeric>
                  {k('lowest')}
                </th>
                <th scope="col" data-numeric>
                  {k('orders')}
                </th>
              </tr>
            </thead>
            <tbody>
              {priceSummary(rows, view).map((row) => (
                <tr key={`${row.key ?? ''}|${row.currency}`}>
                  <td>{row.key ?? k('unknownPort')}</td>
                  <td data-numeric>{money(row.last, row.currency)}</td>
                  <td>{formatDay(row.lastDate)}</td>
                  <td data-numeric>{money(row.min, row.currency)}</td>
                  <td data-numeric>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Panel>
  );
}
